import {
  Card,
  Flex,
  Heading,
  CardBody,
  CardHeader,
  LoadingSpinner,
  TrackScrolledIntoView
} from '@homebotapp/hb-react-component-catalog'
import { useEffect } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { AirDnaBody } from './AirDnaBody'
import { AirDnaBars } from './AirDnaBars'
import { AirDnaEmptyState } from './AirDnaEmptyState'
import { useAppSelector } from '../../../store/hooks'
import { AirDnaAddressForm } from './AirDnaAddressForm'
import { prepareStateKey, fetchAirDna } from '../../../actions/airDna'
import { ROUTE_KEY_BUY } from '../../../constants/routes'
import { selectRouteKey } from '../../../store/selectors/routes'
import { RENTAL_POTENTIAL } from '../../../constants/document_ids'
import { selectCorporateProfile } from '../../../store/selectors/customerProfile'
import { selectHomeId, selectAddress } from '../../../store/selectors/home'
import { getCurrentPath } from '../../../helpers/browser'
import { BUYER_DIGEST_AIRDNA_MODULE } from '../../../constants/tracking/trackingEventLocations/buyerDigestTrackingEventLocation'
import { HOMEOWNER_DIGEST_AIRDNA_MODULE } from '../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'
import { selectHasRequestDataMap } from '../../../store/selectors/airDna'
import { selectClientId } from '../../../store/selectors/client'
import { SEARCH_PATH_HELPERS } from '../../../constants/navigation'
import history from '../../../history'

export const MSG = defineMessages({
  title: {
    id: 'Homeowners.overlay.airDna.learnMore.title',
    defaultMessage: 'Airbnb rental potential'
  },
  error: {
    id: 'AirDna.totally.hosed',
    defaultMessage:
      "Sorry, we don't have short term rental information on this property right now. Please check back soon."
  },
  head: {
    id: 'AirDna.withoutAddress.headline',
    defaultMessage: 'If you rented your home on Airbnb, how much could you potentially earn?'
  },
  earn: {
    id: 'AirDna.headline.howMuchCouldYouEarn',
    defaultMessage: 'If you rented your future home on Airbnb, how much could you potentially earn?'
  },
  loading: {
    id: 'Loading.card.headline',
    defaultMessage: 'Getting the latest data...'
  }
})

export interface AirDnaProps {
  locked?: boolean
  stateKey: string
  hideEdit?: boolean
  showNightlyRentals?: boolean
}

export const AirDna = ({ locked, stateKey, hideEdit, showNightlyRentals }: AirDnaProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const routeKey = useSelector(selectRouteKey)

  const currentPath = getCurrentPath()

  const homeId = useSelector(selectHomeId)
  const address = useSelector(selectAddress)
  const clientId = useSelector(selectClientId)
  const corporateProfile = useSelector(selectCorporateProfile)

  const isAirDnaLearnMorePath =
    currentPath.endsWith('air-dna/learn-more') && !currentPath.includes('short-term-rental-calculator')
  const isAirDnaLearnMoreCalculatorPath = currentPath.endsWith('short-term-rental-calculator/air-dna/learn-more')

  const hasFetchedHomeAirDnaData = useSelector(selectHasRequestDataMap)[homeId]
  const airDna = useAppSelector(state => state.airDna)?.[stateKey]

  useEffect(() => {
    stateKey && dispatch(prepareStateKey(stateKey))
  }, [stateKey, dispatch])

  useEffect(() => {
    if (isAirDnaLearnMorePath && homeId && address && stateKey && !hasFetchedHomeAirDnaData) {
      dispatch(fetchAirDna(homeId, { address: `${address.street} ${address.city}, ${address.state} ${address.zip}` }))
    }
    if (
      isAirDnaLearnMoreCalculatorPath &&
      !homeId &&
      !address &&
      stateKey &&
      Object.keys(airDna?.data || {}).length === 0
    ) {
      history.push(SEARCH_PATH_HELPERS.shortTermRentalCalculator.buildPath(clientId))
    }
  }, [
    dispatch,
    homeId,
    address,
    stateKey,
    isAirDnaLearnMorePath,
    hasFetchedHomeAirDnaData,
    isAirDnaLearnMoreCalculatorPath,
    clientId,
    airDna
  ])

  if (!corporateProfile?.showNightlyRentals && !showNightlyRentals) {
    return null
  }

  const getHeaderText = () => {
    if (airDna?.loading) {
      return MSG.loading
    } else if (airDna?.error) {
      return MSG.error
    } else if (!hideEdit && !airDna?.params) {
      return MSG.earn
    } else {
      return MSG.head
    }
  }

  const getBody = () => {
    if (airDna?.loading) {
      return (
        <Flex justify='center' align='center' marginBottom={8}>
          <LoadingSpinner />
        </Flex>
      )
    } else if (airDna?.error) {
      return <AirDnaBars />
    } else if (!hideEdit && !airDna?.params) {
      return (
        <>
          <AirDnaAddressForm stateKey={stateKey} />
          <AirDnaEmptyState stateKey={stateKey} />
        </>
      )
    } else if (airDna?.params) {
      return (
        <AirDnaBody locked={locked} stateKey={stateKey} corporateProfile={corporateProfile} params={airDna.params} />
      )
    } else {
      return <AirDnaEmptyState stateKey={stateKey} />
    }
  }

  return (
    <TrackScrolledIntoView
      tracking={{
        guid: 'ckVxxhFetkgj7dzWkrwqzY',
        descriptor: getHeaderText().defaultMessage,
        ui_context: routeKey === ROUTE_KEY_BUY ? BUYER_DIGEST_AIRDNA_MODULE : HOMEOWNER_DIGEST_AIRDNA_MODULE
      }}
    >
      <Card
        size='lg'
        data-qa='AirDnaCard'
        id={RENTAL_POTENTIAL}
        scrollMarginTop='var(--digest-module-scroll-margin-top)'
      >
        <CardHeader textAlign='center' pb={0}>
          <Heading fontWeight='light' size='md' marginBottom={0}>
            {intl.formatMessage(getHeaderText())}
          </Heading>
        </CardHeader>
        <CardBody overflow='hidden'>{getBody()}</CardBody>
      </Card>
    </TrackScrolledIntoView>
  )
}
